// src/pages/performance/MyAppraisalTasksPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
  Badge,
  Accordion,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TimelineIcon from "@mui/icons-material/Timeline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditNoteIcon from "@mui/icons-material/EditNote";
import InfoIcon from "@mui/icons-material/Info";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import { PageHeader, PageLayout } from "../../components/ui";

const API = {
  appraisals: "/performance/appraisals",
  stats: "/performance/appraisals/stats/my-tasks",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending", color: "warning" },
  { value: "in_progress", label: "In Progress", color: "info" },
  { value: "completed", label: "Completed", color: "success" },
];

const TASK_TYPES = [
  { value: "", label: "All Tasks" },
  { value: "self", label: "Self Review" },
  { value: "evaluate", label: "Evaluate Others" },
];

const TASK_TAB_OPTIONS = [
  { value: "inbox", label: "Inbox", countKey: "inbox_count" },
  { value: "history", label: "History", countKey: "history_count" },
  { value: "all", label: "All Tasks", countKey: "all_count" },
];

const DEFAULT_STATS = {
  inbox_count: 0,
  history_count: 0,
  all_count: 0,
  my_tasks_pending_current: 0,
  my_tasks_pending_any: 0,
  assigned_in_progress: 0,
  assigned_completed: 0,
  breakdown: {},
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

/* ✅ rating helpers (supports multiple backend keys safely) */
const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatRating = (v) => {
  const n = toNumberOrNull(v);
  if (n === null) return "—";
  return n.toFixed(1);
};

const getCurrentOverallRating = (row) =>
  row?.attributes?.overall_rating ??
  row?.attributes?.overall_score ??
  row?.overall_rating ??
  row?.overall_score ??
  row?.overallRating ??
  row?.overallScore ??
  null;

const getPreviousOverallRating = (row) =>
  row?.attributes?.previous_overall_rating ??
  row?.attributes?.prev_overall_rating ??
  row?.previous_overall_rating ??
  row?.prev_overall_rating ??
  row?.previousOverallRating ??
  row?.prevOverallRating ??
  null;

const getSteps = (row) => row?.step_instances || row?.stepInstances || [];

const getCurrentStep = (row) => {
  const steps = getSteps(row);
  if (!steps.length) return null;

  const currentOrder = row?.current_step_order;
  let current = null;

  if (currentOrder != null) {
    current = steps.find((s) => Number(s.step_order) === Number(currentOrder));
  }

  if (!current) {
    current = steps.find((s) => ["in_progress", "pending"].includes(String(s.status)));
  }

  return current || steps[steps.length - 1] || null;
};

const getProgressInfo = (row) => {
  const steps = getSteps(row);
  if (!steps.length) return { completed: 0, total: 0, percent: 0 };

  const total = steps.length;
  const completed = steps.filter((s) =>
    ["completed", "submitted"].includes(String(s.status))
  ).length;
  const percent = total ? Math.round((completed / total) * 100) : 0;

  return { completed, total, percent };
};

const isSelfReviewTask = (row) => {
  const empCode = row?.employee?.employee_code || row?.employee_code;
  if (!empCode) return false;

  const currentStep = getCurrentStep(row);
  const stepUserCode = currentStep?.user?.employee_code;

  return !!currentStep && !!stepUserCode && String(stepUserCode) === String(empCode);
};

const getTaskRoute = (row) => {
  const id = row?.id;
  if (!id) return "#";
  return isSelfReviewTask(row)
    ? `/performance/appraisals/${id}/evaluate`
    : `/performance/appraisals/${id}/evaluate`;
};

const prettyStatus = (s) => {
  if (!s) return "—";
  return String(s).replaceAll("_", " ");
};

function AppraisalTaskPulsePanel({ inboxActionCount, stats, statsLoading, taskTab }) {
  const theme = useTheme();
  const activeTab = TASK_TAB_OPTIONS.find((option) => option.value === taskTab);
  const activeLabel = activeTab?.label || "Inbox";
  const historyCount = Number(stats?.history_count || 0);
  const inProgressCount = Number(stats?.assigned_in_progress || 0);
  const allCount = Number(stats?.all_count || 0);
  const pulseItems = [
    {
      label: "Current view",
      value: activeLabel,
      caption: "Task tab selected",
      color: theme.palette.primary.main,
    },
    {
      label: "Needs action",
      value: statsLoading ? "..." : inboxActionCount,
      caption: inboxActionCount ? "Waiting on your review" : "No pending task",
      color: inboxActionCount ? theme.palette.warning.main : theme.palette.success.main,
    },
    {
      label: "In progress",
      value: statsLoading ? "..." : inProgressCount,
      caption: "Open appraisal workflows",
      color: theme.palette.info.main,
    },
    {
      label: "Completed",
      value: statsLoading ? "..." : historyCount,
      caption: `${allCount} total assigned`,
      color: theme.palette.success.main,
    },
  ];

  const heroAccent = inboxActionCount ? theme.palette.warning.main : theme.palette.primary.main;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        mb: 1.4,
        borderRadius: "8px",
        border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.18)}`,
        boxShadow: theme.hr?.shadow?.soft || theme.shadows[1],
        background: `radial-gradient(1100px 460px at -6% -30%, ${alpha(
          heroAccent,
          0.13
        )} 0%, transparent 60%),
          radial-gradient(900px 420px at 108% 0%, ${alpha(theme.palette.info.main, 0.1)} 0%, transparent 58%),
          linear-gradient(150deg, ${alpha(
            theme.palette.background.paper,
            theme.palette.mode === "dark" ? 0.86 : 0.97
          )} 0%, ${alpha(
          theme.palette.background.paper,
          theme.palette.mode === "dark" ? 0.7 : 0.92
        )} 100%)`,
        /* animated accent line on top */
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg,
            ${alpha(heroAccent, 0.25)} 0%,
            ${heroAccent} 32%,
            ${theme.palette.primary.main} 66%,
            ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
          backgroundSize: "220% 100%",
          animation: "pulseAccentSlide 6s ease-in-out infinite",
        },
        "@keyframes pulseAccentSlide": {
          "0%, 100%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          p: { xs: 1.3, md: 1.6 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(280px, 0.85fr) minmax(0, 1.15fr)" },
          gap: { xs: 1.4, lg: 1.6 },
          alignItems: "center",
        }}
      >
        {/* identity */}
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                width: 52,
                height: 52,
                borderRadius: "8px",
                bgcolor: alpha(heroAccent, 0.12),
                color: heroAccent,
                boxShadow: `0 6px 16px ${alpha(heroAccent, 0.2)}`,
                flexShrink: 0,
              }}
            >
              <InboxRoundedIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 850,
                  letterSpacing: -0.4,
                  lineHeight: 1.1,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "1.2rem", md: "1.45rem" },
                }}
              >
                Appraisal workbench
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Review assigned appraisal steps and jump into evaluation.
              </Typography>
            </Box>
          </Stack>

          {/* meta pills */}
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ flexWrap: "wrap", rowGap: 0.75 }}
          >
            <Chip
              size="small"
              icon={<TimelineIcon sx={{ fontSize: 15 }} />}
              label="Performance"
              sx={{
                height: 28,
                borderRadius: "8px",
                fontWeight: 800,
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            />
            <Chip
              size="small"
              label={`View: ${activeLabel}`}
              sx={{
                height: 28,
                borderRadius: "8px",
                fontWeight: 800,
                border: `1px solid ${alpha(theme.palette.divider, 0.24)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.7),
              }}
            />
            <Chip
              size="small"
              icon={
                inboxActionCount ? (
                  <PendingActionsIcon sx={{ fontSize: 15, color: `${theme.palette.warning.main} !important` }} />
                ) : (
                  <CheckCircleIcon sx={{ fontSize: 15, color: `${theme.palette.success.main} !important` }} />
                )
              }
              label={
                statsLoading
                  ? "..."
                  : inboxActionCount
                  ? `${inboxActionCount} need action`
                  : "All clear"
              }
              sx={{
                height: 28,
                borderRadius: "8px",
                fontWeight: 800,
                color: inboxActionCount ? theme.palette.warning.main : theme.palette.success.main,
                bgcolor: alpha(
                  inboxActionCount ? theme.palette.warning.main : theme.palette.success.main,
                  0.1
                ),
                border: `1px solid ${alpha(
                  inboxActionCount ? theme.palette.warning.main : theme.palette.success.main,
                  0.22
                )}`,
              }}
            />
          </Stack>
        </Stack>

        {/* pulse stat tiles */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            gap: 0.85,
          }}
        >
          {pulseItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                position: "relative",
                height: "100%",
                minHeight: 86,
                p: 1.1,
                borderRadius: "8px",
                border: `1px solid ${alpha(item.color, 0.2)}`,
                bgcolor: alpha(
                  theme.palette.background.paper,
                  theme.palette.mode === "dark" ? 0.5 : 0.82
                ),
                backdropFilter: "blur(6px)",
                transition: "transform 160ms ease, box-shadow 180ms ease, border-color 180ms ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  borderColor: alpha(item.color, 0.4),
                  boxShadow: `0 8px 18px ${alpha(item.color, 0.14)}`,
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 10,
                  right: 10,
                  top: 0,
                  height: 1,
                  background: `linear-gradient(90deg, ${alpha(item.color, 0)} 0%, ${alpha(
                    item.color,
                    0.4
                  )} 50%, ${alpha(item.color, 0)} 100%)`,
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 850, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}
              >
                {item.label}
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.15, fontWeight: 900, lineHeight: 1, color: item.color }}>
                {item.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.caption}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function TaskMetricCard({ icon, title, value, hint, color, loading, onClick }) {
  const theme = useTheme();
  const [paletteKey, shadeKey] = String(color || "primary.main").split(".");
  const accent = theme.palette?.[paletteKey]?.[shadeKey] || theme.palette.primary.main;

  return (
    <Paper
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      variant="outlined"
      onClick={onClick}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        minHeight: 88,
        p: 1.15,
        borderRadius: "8px",
        borderColor: alpha(accent, 0.2),
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.58 : 0.94),
        color: "text.primary",
        cursor: onClick ? "pointer" : "default",
        font: "inherit",
        textAlign: "left",
        transition: "transform 150ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 12,
          right: 12,
          top: 0,
          height: 1,
          background: `linear-gradient(90deg, ${alpha(accent, 0)} 0%, ${alpha(accent, 0.4)} 50%, ${alpha(
            accent,
            0
          )} 100%)`,
        },
        "&:hover": onClick
          ? {
              transform: "translateY(-2px)",
              borderColor: alpha(accent, 0.42),
              bgcolor: alpha(accent, theme.palette.mode === "dark" ? 0.1 : 0.045),
              boxShadow: `0 8px 18px ${alpha(accent, 0.16)}`,
            }
          : undefined,
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            color: accent,
            bgcolor: alpha(accent, 0.12),
            boxShadow: `0 4px 12px ${alpha(accent, 0.16)}`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 850, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}
          >
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1, color: accent }}>
            {loading ? "..." : value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const MyAppraisalTasksPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");

  const [stats, setStats] = useState(DEFAULT_STATS);

  const [taskTab, setTaskTab] = useState("inbox");
  const [statusFilter, setStatusFilter] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const borderColor = useMemo(() => alpha(theme.palette.divider, 0.8), [theme]);
  const inboxActionCount = statsLoading
    ? 0
    : Number(stats?.inbox_count ?? stats?.my_tasks_pending_current ?? 0) || 0;

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowExpand = (id) => setExpandedRow((prev) => (prev === id ? null : id));

  const getStepIcon = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "completed":
      case "submitted":
        return <CheckCircleIcon color="success" fontSize="small" />;
      case "in_progress":
        return <TimelineIcon color="info" fontSize="small" />;
      case "pending":
        return <PendingActionsIcon color="warning" fontSize="small" />;
      case "not_started":
        return <AssignmentIcon color="action" fontSize="small" />;
      default:
        return <AssignmentIcon color="disabled" fontSize="small" />;
    }
  };

  const getStepStatusText = (status) => {
    const statusMap = {
      pending: "Pending",
      not_started: "Not Started",
      in_progress: "In Progress",
      submitted: "Submitted",
      completed: "Completed",
      unassigned: "Unassigned",
      skipped: "Skipped",
    };
    const key = String(status || "").toLowerCase();
    return statusMap[key] || status;
  };

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await api.get(API.stats);
      setStats({ ...DEFAULT_STATS, ...(res?.data || {}) });
      if (typeof window.refreshAppraisalTasksCount === "function") {
        window.refreshAppraisalTasksCount();
      }
    } catch (err) {
      console.error("Failed to load stats", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchAppraisals = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        status: statusFilter || undefined,
        task_type: taskTypeFilter || undefined,
        my_tasks: 1,
        task_scope: taskTab,
      };

      const res = await api.get(API.appraisals, { params });
      const payload = res?.data || {};
      const data = payload?.data || [];

      setRows(Array.isArray(data) ? data : []);
      setTotalRows(Number(payload?.total || 0));
    } catch (err) {
      console.error("Failed to load tasks", err);
      setError(err?.response?.data?.message || "Failed to load tasks");
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, taskTypeFilter, taskTab]);

  const handleRefresh = () => {
    fetchStats();
    fetchAppraisals();
  };

  const handleTaskTabChange = (event, newValue) => {
    setTaskTab(newValue);
    setPage(0);
    // DO NOT call fetchAppraisals here (state updates are async). useEffect will handle it.
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchAppraisals();
  }, [fetchAppraisals]);

  return (
    <PageLayout backgroundAccent compact>
      <PageHeader
        compact
        overline="Performance"
        title="Appraisal task queue"
        subtitle={
          inboxActionCount > 0
            ? `${inboxActionCount} appraisal task(s) need your action.`
            : "No appraisal tasks are currently pending your action."
        }
        actions={[
          inboxActionCount > 0 ? (
            <Button key="inbox" variant="outlined" color="inherit" onClick={() => setTaskTab("inbox")}>
              Open Inbox
            </Button>
          ) : null,
          <Button key="refresh" variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading || statsLoading}>
            Refresh
          </Button>,
        ].filter(Boolean)}
      />

      <AppraisalTaskPulsePanel
        inboxActionCount={inboxActionCount}
        stats={stats}
        statsLoading={statsLoading}
        taskTab={taskTab}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: 0.85,
          mb: 1.25,
        }}
      >
          <TaskMetricCard
            icon={<AssignmentIcon fontSize="small" />}
            title="Inbox"
            value={stats.inbox_count}
            hint="Action required"
            color="warning.main"
            loading={statsLoading}
            onClick={() => setTaskTab("inbox")}
          />
          <TaskMetricCard
            icon={<CheckCircleIcon fontSize="small" />}
            title="History"
            value={stats.history_count}
            hint="Completed by you"
            color="success.main"
            loading={statsLoading}
            onClick={() => setTaskTab("history")}
          />
          <TaskMetricCard
            icon={<TimelineIcon fontSize="small" />}
            title="In Progress"
            value={stats.assigned_in_progress}
            hint="Open workflows"
            color="info.main"
            loading={statsLoading}
          />
          <TaskMetricCard
            icon={<GroupsIcon fontSize="small" />}
            title="All Tasks"
            value={stats.all_count}
            hint="Total assigned"
            color="primary.main"
            loading={statsLoading}
            onClick={() => setTaskTab("all")}
          />
      </Box>

      {/* Task Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.6,
          borderRadius: "8px",
          border: `1px solid ${borderColor}`,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.72 : 0.9),
          boxShadow: theme.hr?.shadow?.soft || theme.shadows[2],
          overflow: "hidden",
        }}
      >
        <Tabs
          value={taskTab}
          onChange={handleTaskTabChange}
          sx={{
            borderBottom: `1px solid ${borderColor}`,
            px: 1,
            pt: 1,
            minHeight: 48,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: 0.75 },
            "& .MuiTab-root": {
              minHeight: 38,
              borderRadius: "8px",
              fontWeight: 800,
              textTransform: "none",
              color: "text.secondary",
            },
            "& .Mui-selected": {
              color: `${theme.palette.primary.contrastText} !important`,
              bgcolor: theme.palette.primary.main,
            },
          }}
        >
          {TASK_TAB_OPTIONS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <Badge
                  badgeContent={statsLoading ? 0 : stats[tab.countKey] || 0}
                  color="primary"
                  max={99}
                  sx={{ "& .MuiBadge-badge": { fontSize: "0.7rem", height: 20, minWidth: 20 } }}
                >
                  {tab.label}
                </Badge>
              }
            />
          ))}
        </Tabs>

        <Box sx={{ p: { xs: 1.35, sm: 1.65 } }}>
          {/* Filters */}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <FilterAltRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 850,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "text.secondary",
              }}
            >
              Filters
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 2fr" },
              gap: 1.2,
              alignItems: "center",
              mb: 1.2,
            }}
          >
            <Box>
              <TextField
                fullWidth
                size="small"
                select
                label="Filter by Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  },
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={opt.label}
                        size="small"
                        color={opt.color || "default"}
                        sx={{ minWidth: 90, borderRadius: "8px" }}
                      />
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <TextField
                fullWidth
                size="small"
                select
                label="Task Type"
                value={taskTypeFilter}
                onChange={(e) => {
                  setTaskTypeFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  },
                }}
              >
                {TASK_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {type.value === "self" && <PersonIcon fontSize="small" />}
                      {type.value === "evaluate" && <GroupsIcon fontSize="small" />}
                      <span>{type.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ gridColumn: { xs: "1 / -1", md: "3 / span 2" } }}>
              <Stack direction="row" spacing={1} justifyContent={{ xs: "stretch", md: "flex-end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth={false}
                  onClick={() => {
                    setStatusFilter("");
                    setTaskTypeFilter("");
                    setPage(0);
                  }}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 700,
                    width: { xs: "100%", md: "auto" },
                    transition: "transform 150ms ease, box-shadow 180ms ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.14)}`,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Stack>
            </Box>
          </Box>

          {/* Error Message */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: "8px",
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                backgroundColor: alpha(theme.palette.error.main, 0.06),
              }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Main Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: `1px solid ${borderColor}`,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.72 : 0.92),
          boxShadow: theme.hr?.shadow?.soft || theme.shadows[2],
        }}
      >
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "& .MuiTableCell-root": {
                  fontWeight: 850,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "text.secondary",
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.28)}`,
                },
              }}
            >
              <TableRow>
                <TableCell width={40} />
                <TableCell>Employee</TableCell>
                <TableCell width={120}>Task Type</TableCell>
                <TableCell width={140}>Workflow</TableCell>
                <TableCell width={120}>Status</TableCell>
                <TableCell width={140}>Overall</TableCell>
                <TableCell width={180}>Current Step</TableCell>
                <TableCell width={200}>Progress</TableCell>
                <TableCell width={120}>Created</TableCell>
                <TableCell width={110}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, border: 0 }}>
                    <Stack spacing={2} alignItems="center">
                      <CircularProgress size={44} thickness={4} />
                      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                        Loading your tasks...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 7, border: 0, color: "text.secondary" }}>
                    <Stack spacing={2} alignItems="center">
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 76,
                          height: 76,
                          borderRadius: "8px",
                          color: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          border: `2px dashed ${alpha(theme.palette.primary.main, 0.28)}`,
                        }}
                      >
                        <AssignmentIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Box sx={{ textAlign: "center", maxWidth: 360 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }} gutterBottom>
                          No tasks found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {taskTab === "inbox"
                            ? "You don't have any pending tasks requiring action."
                            : "No tasks found for the selected filters."}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const currentStep = getCurrentStep(row);
                  const progress = getProgressInfo(row);
                  const steps = getSteps(row);
                  const isSelf = isSelfReviewTask(row);
                  const isExpanded = expandedRow === row.id;
                  const taskState = row.my_task_state || "not_assigned_now";

                  const overall = getCurrentOverallRating(row);
                  const prevOverall = getPreviousOverallRating(row);

                  return (
                    <React.Fragment key={row.id}>
                      {/* Main Row */}
                      <TableRow
                        hover
                        sx={{
                          transition: "background-color 160ms ease",
                          bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : "inherit",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, isExpanded ? 0.05 : 0.025),
                          },
                          "& > .MuiTableCell-root": {
                            borderBottom: isExpanded
                              ? "none"
                              : `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                          },
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleRowExpand(row.id)}
                            sx={{
                              transform: isExpanded ? "rotate(180deg)" : "none",
                              transition: "transform 180ms ease",
                            }}
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              variant="rounded"
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: "8px",
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                fontWeight: 800,
                                boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.16)}`,
                              }}
                            >
                              {row.employee?.name?.[0]?.toUpperCase() || "U"}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {row.employee?.name || "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.employee?.employee_code || "—"}
                                {row.employee?.department && ` • ${row.employee.department}`}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={isSelf ? "Self Review" : "Evaluate"}
                            size="small"
                            color={isSelf ? "info" : "primary"}
                            icon={
                              isSelf ? (
                                <PersonIcon fontSize="small" />
                              ) : (
                                <GroupsIcon fontSize="small" />
                              )
                            }
                            variant="outlined"
                            sx={{ borderRadius: "8px", fontWeight: 700 }}
                          />
                        </TableCell>

                        <TableCell>
                          <Tooltip
                            title={row.workflow_template?.name || row.workflowTemplate?.name || ""}
                            placement="top"
                          >
                            <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                              {row.workflow_template?.name || row.workflowTemplate?.name || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={prettyStatus(row.status)}
                            size="small"
                            color={
                              row.status === "completed"
                                ? "success"
                                : row.status === "in_progress"
                                ? "info"
                                : row.status === "pending"
                                ? "warning"
                                : "default"
                            }
                            sx={{ borderRadius: "8px", fontWeight: 700, textTransform: "capitalize" }}
                          />
                        </TableCell>

                        {/* Overall rating */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={700}>
                              {formatRating(overall)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Prev: {formatRating(prevOverall)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          {currentStep ? (
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                Step {currentStep.step_order}
                              </Typography>
                              {currentStep.user?.name && (
                                <Typography variant="caption" color="text.secondary">
                                  {currentStep.user.name}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        <TableCell>
                          {progress.total > 0 ? (
                            <Box>
                              <LinearProgress
                                variant="determinate"
                                value={progress.percent}
                                sx={{
                                  height: 8,
                                  borderRadius: 999,
                                  mb: 0.5,
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${
                                      theme.palette.secondary?.main || theme.palette.primary.dark
                                    })`,
                                  },
                                }}
                              />
                              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="caption" color="text.secondary">
                                  {progress.completed}/{progress.total} steps
                                </Typography>
                                <Typography variant="caption" fontWeight={800} color="primary">
                                  {progress.percent}%
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="caption">{formatDateOnly(row.created_at)}</Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/performance/appraisals/${row.id}`)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => navigate(getTaskRoute(row))}
                              sx={{
                                minWidth: 0,
                                px: 1.5,
                                borderRadius: "8px",
                                fontWeight: 800,
                                transition: "transform 150ms ease, box-shadow 180ms ease",
                                "&:hover": {
                                  transform: "translateY(-1px)",
                                  boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.28)}`,
                                },
                              }}
                            >
                              {isSelf ? "Review" : "Start"}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Details Row */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ p: 0, borderTop: 0 }}>
                            <Accordion
                              expanded={isExpanded}
                              sx={{
                                boxShadow: "none",
                                "&:before": { display: "none" },
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                              }}
                            >
                              <AccordionDetails sx={{ pt: 2, pb: 3 }}>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                    gap: 3,
                                  }}
                                >
                                  <Box>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        height: "100%",
                                        p: 2,
                                        borderRadius: "8px",
                                        border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
                                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                        sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}
                                      >
                                        <TimelineIcon fontSize="small" color="info" />
                                        Workflow Steps
                                      </Typography>

                                      <List dense>
                                        {steps.length === 0 ? (
                                          <ListItem>
                                            <ListItemText
                                              primary="No steps defined"
                                              secondary="Workflow steps will be added when the appraisal starts"
                                            />
                                          </ListItem>
                                        ) : (
                                          steps
                                            .slice()
                                            .sort(
                                              (a, b) => (a.step_order || 0) - (b.step_order || 0)
                                            )
                                            .map((step, index) => {
                                              const isCurrent = step.id === currentStep?.id;
                                              const isCompleted = ["completed", "submitted"].includes(
                                                step.status
                                              );
                                              const isPending = step.status === "pending";

                                              return (
                                                <ListItem
                                                  key={step.id || index}
                                                  sx={{
                                                    pl: 2,
                                                    border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                                                    borderLeft: isCurrent
                                                      ? `3px solid ${theme.palette.primary.main}`
                                                      : `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                                                    bgcolor: isCurrent
                                                      ? alpha(theme.palette.primary.main, 0.05)
                                                      : "transparent",
                                                    borderRadius: "8px",
                                                    mb: 1,
                                                  }}
                                                >
                                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <Badge
                                                      badgeContent={step.step_order}
                                                      color={
                                                        isCurrent
                                                          ? "primary"
                                                          : isCompleted
                                                          ? "success"
                                                          : isPending
                                                          ? "warning"
                                                          : "default"
                                                      }
                                                    >
                                                      {getStepIcon(step.status)}
                                                    </Badge>
                                                  </ListItemIcon>

                                                  <ListItemText
                                                    primaryTypographyProps={{ component: "div" }}
                                                    secondaryTypographyProps={{ component: "div" }}
                                                    primary={
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: 1,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          fontWeight={isCurrent ? "medium" : "regular"}
                                                        >
                                                          {step.step_name ||
                                                            step.title ||
                                                            `Step ${step.step_order}`}
                                                        </Typography>

                                                        {isCurrent && (
                                                          <Chip
                                                            label="Current"
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                          />
                                                        )}
                                                      </Box>
                                                    }
                                                    secondary={
                                                      <Box sx={{ mt: 0.5 }}>
                                                        <Typography
                                                          variant="caption"
                                                          color="text.secondary"
                                                        >
                                                          {step.user?.name
                                                            ? `Assigned to: ${step.user.name}`
                                                            : "Unassigned"}
                                                        </Typography>

                                                        <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                                                          <Chip
                                                            label={getStepStatusText(step.status)}
                                                            size="small"
                                                            color={
                                                              step.status === "completed"
                                                                ? "success"
                                                                : step.status === "pending"
                                                                ? "warning"
                                                                : step.status === "in_progress"
                                                                ? "info"
                                                                : "default"
                                                            }
                                                            variant="outlined"
                                                          />

                                                          {step.submitted_at && (
                                                            <Typography
                                                              variant="caption"
                                                              color="text.secondary"
                                                            >
                                                              Submitted:{" "}
                                                              {formatDateTime(step.submitted_at)}
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    }
                                                  />
                                                </ListItem>
                                              );
                                            })
                                        )}
                                      </List>
                                    </Paper>
                                  </Box>

                                  <Box>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        height: "100%",
                                        p: 2,
                                        borderRadius: "8px",
                                        border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
                                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                        sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}
                                      >
                                        <InfoIcon fontSize="small" color="primary" />
                                        Task Details
                                      </Typography>

                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: "1fr 1fr",
                                          gap: 1.5,
                                        }}
                                      >
                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}
                                          >
                                            Appraisal ID
                                          </Typography>
                                          <Typography variant="body2" fontWeight={700}>
                                            #{row.id}
                                          </Typography>
                                        </Box>

                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}
                                          >
                                            Current Step Order
                                          </Typography>
                                          <Typography variant="body2">
                                            {row.current_step_order || "—"}
                                          </Typography>
                                        </Box>

                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}
                                          >
                                            Created
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatDateTime(row.created_at)}
                                          </Typography>
                                        </Box>

                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}
                                          >
                                            Updated
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatDateTime(row.updated_at)}
                                          </Typography>
                                        </Box>

                                        <Box sx={{ gridColumn: "1 / -1" }}>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}
                                          >
                                            Period
                                          </Typography>
                                          <Typography variant="body2">
                                            {row.attributes?.period || row.period || "—"}
                                          </Typography>
                                        </Box>

                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}
                                          >
                                            Overall Rating
                                          </Typography>
                                          <Typography variant="body2" fontWeight={700} color="primary">
                                            {formatRating(overall)}
                                          </Typography>
                                        </Box>

                                        <Box>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}
                                          >
                                            Previous Overall
                                          </Typography>
                                          <Typography variant="body2">
                                            {formatRating(prevOverall)}
                                          </Typography>
                                        </Box>
                                      </Box>

                                      {taskState && (
                                        <Paper
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            mt: 2,
                                            borderRadius: "8px",
                                            bgcolor: alpha(
                                              taskState === "action_required"
                                                ? theme.palette.warning.light
                                                : taskState === "completed_by_me"
                                                ? theme.palette.success.light
                                                : theme.palette.grey[200],
                                              0.1
                                            ),
                                            borderColor: alpha(
                                              taskState === "action_required"
                                                ? theme.palette.warning.main
                                                : taskState === "completed_by_me"
                                                ? theme.palette.success.main
                                                : theme.palette.grey[400],
                                              0.3
                                            ),
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            gutterBottom
                                            sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}
                                          >
                                            <InfoIcon
                                              color={taskState === "action_required" ? "warning" : "success"}
                                              fontSize="small"
                                            />
                                            Your Task Status
                                          </Typography>

                                          <Typography variant="body2">
                                            {taskState === "action_required"
                                              ? "⚠️ Action required - This task is waiting for your review"
                                              : taskState === "completed_by_me"
                                              ? "✅ You have completed your part in this appraisal"
                                              : "This task is not currently assigned to you"}
                                          </Typography>

                                          {row.my_last_action_at && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ mt: 1, display: "block" }}
                                            >
                                              Your last action: {formatDateTime(row.my_last_action_at)}
                                            </Typography>
                                          )}
                                        </Paper>
                                      )}

                                      {currentStep && (
                                        <Button
                                          fullWidth
                                          variant="contained"
                                          size="medium"
                                          startIcon={<EditNoteIcon />}
                                          onClick={() => navigate(getTaskRoute(row))}
                                          sx={{
                                            mt: 2,
                                            borderRadius: "8px",
                                            fontWeight: 800,
                                            transition: "transform 150ms ease, box-shadow 180ms ease",
                                            "&:hover": {
                                              transform: "translateY(-1px)",
                                              boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.28)}`,
                                            },
                                          }}
                                        >
                                          {isSelf ? "Start Self Review" : "Start Evaluation"}
                                        </Button>
                                      )}
                                    </Paper>
                                  </Box>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.24)}`,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontWeight: 600,
            },
          }}
        />
      </Paper>

      {/* NOTE:
        This UI expects your list endpoint (/performance/appraisals) to include:
        attributes.overall_rating (or overall_rating) AND attributes.previous_overall_rating (or previous_overall_rating).
        If you only added it in show(), the table will display "—" until you also add it to index/list payload.
      */}
    </PageLayout>
  );
};

export default MyAppraisalTasksPage;
